import { BaseParser } from '../BaseParser';

/**
 * BMO (Bank of Montreal) Parser
 * Handles BMO checking account CSV exports
 */
export class BMOParser extends BaseParser {
  constructor() {
    super();
    this.name = 'BMOParser';
    this.priority = 10;
  }

  /**
   * Detect if this is a BMO CSV file
   */
  detect(header, bank) {
    let confidence = 0;

    // Bank hint from folder structure
    if (bank === 'bmo' || bank === 'bank of montreal') {
      confidence += 50;
    }

    // BMO format: POSTED DATE,DESCRIPTION,AMOUNT,CURRENCY,TRANSACTION REFERENCE NUMBER,...
    // Very distinctive with CURRENCY, FI TRANSACTION REFERENCE, CREDIT/DEBIT columns
    if (header.includes('posted date') &&
        header.includes('currency') &&
        header.includes('fi transaction reference') &&
        header.includes('credit/debit')) {
      confidence += 50;
    }

    return confidence;
  }

  /**
   * Parse BMO CSV
   * Format: POSTED DATE, DESCRIPTION, AMOUNT, CURRENCY, TRANSACTION REFERENCE NUMBER,
   *         FI TRANSACTION REFERENCE, TYPE, CREDIT/DEBIT, ORIGINAL AMOUNT
   *
   * Date format: MM/DD/YYYY
   * Amount: Negative = debit/expense, Positive = credit/income (already correct sign)
   * Type: DDA Debit, DDA Deposit, etc.
   */
  parse(lines, filename) {
    const transactions = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue;

      try {
        const row = this.parseCSVLine(line);
        if (row.length < 9) continue;

        const [
          postedDate,
          description,
          amount,
          currency,
          transactionRefNum,
          fiTransactionRef,
          type,
          creditDebit,
          originalAmount
        ] = row;

        // Skip if no description or amount
        if (!description || !amount) continue;

        const amountValue = this.parseAmount(amount);

        // Determine transaction type based on CREDIT/DEBIT column and TYPE
        const transactionType = this.inferTransactionType(creditDebit, type, description);

        transactions.push(this.createTransaction({
          id: this.generateId(postedDate, description, amount),
          date: this.parseDate(postedDate),
          postDate: this.parseDate(postedDate),
          description: description || 'Unknown',
          amount: amountValue,
          type: transactionType,
          category: 'Uncategorized', // Will be handled by categorization pipeline
          memo: `${type} - Ref: ${transactionRefNum || fiTransactionRef}`,
          source: filename || 'BMO',
          rawData: row
        }));
      } catch (error) {
        this.log(`Error parsing line ${i}: ${error.message}`, 'error');
      }
    }

    return transactions;
  }

  /**
   * Infer transaction type based on BMO's CREDIT/DEBIT and TYPE columns
   */
  inferTransactionType(creditDebit, type, description) {
    const desc = description.toLowerCase();

    // Check for common transaction types based on description
    if (desc.includes('payroll') || desc.includes('salary')) {
      return 'Deposit';
    }

    if (desc.includes('transfer')) {
      return 'Transfer';
    }

    if (desc.includes('withdrawal') || desc.includes('atm')) {
      return 'Withdrawal';
    }

    if (desc.includes('fee') || desc.includes('charge')) {
      return 'Fee';
    }

    if (desc.includes('interest')) {
      return 'Interest';
    }

    // Fall back to BMO's TYPE field
    if (type.includes('Deposit')) {
      return 'Deposit';
    }

    if (type.includes('Debit')) {
      return 'Debit';
    }

    // Default based on CREDIT/DEBIT indicator
    if (creditDebit && creditDebit.toLowerCase() === 'credit') {
      return 'Credit';
    } else if (creditDebit && creditDebit.toLowerCase() === 'debit') {
      return 'Debit';
    }

    return 'Unknown';
  }

  getInfo() {
    return {
      name: this.name,
      priority: this.priority,
      supportedBanks: ['bmo', 'bank of montreal'],
      formats: ['BMO Checking Account'],
      dateFormat: 'MM/DD/YYYY',
      notes: 'Handles BMO CSV exports with POSTED DATE, DESCRIPTION, AMOUNT, etc.'
    };
  }
}
